import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFieldArray, useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ReactDOM from "react-dom";

import NumberInput from "@components/input/NumberInput";

const Container = styled.div({
  position: "relative",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string; isDisabled?: boolean }>(
  ({ theme, selectedValue, isDisabled }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",
    border: `1px solid ${isDisabled ? "#f4f4f4" : theme.color.light_gray}`,
    backgroundColor: isDisabled ? "#f4f4f4" : "transparent",
    color: isDisabled
      ? "#bdbdbd"
      : selectedValue === "선택" || selectedValue === "Select"
        ? theme.color.light_gray
        : theme.color.black,
    cursor: isDisabled ? "not-allowed" : "pointer",
  }),
);

const SelectBox = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== "openUpward",
})<{
  top: number;
  left: number;
  width: number;
  openUpward: boolean;
}>(({ theme, top, left, width, openUpward }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "fixed",
  top: `${top}px`,
  left: `${left}px`,
  width: `${width}px`,
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
}));

const OptionItem = styled.button<{ last: boolean; selected: boolean }>(
  ({ theme, last, selected }) => ({
    width: "100%",
    padding: "10px 30px 10px 20px",
    display: "flex",
    justifyContent: "space-between",
    gap: "1rem",
    alignItems: "center",
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    fontWeight: theme.fontWeight.regular,
    color: selected ? theme.color.green : theme.color.black,
    backgroundColor: theme.color.white,
    border: "none",
    textAlign: "start",
    borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
    ":hover": {
      backgroundColor: theme.color.border_gray,
    },
  }),
);

const ChangeQuantityContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  paddingTop: "1.6rem",
  gap: "1.6rem",
});

const QuantityBox = styled.div({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "1rem",
  padding: "0.5rem 1rem",
});

const QuantityInputBox = styled.div({
  display: "flex",
  gap: "1rem",
  alignItems: "center",
});

type Props = {
  options: any[];
  name: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  disabled?: boolean;
};

const SizeDropdown = ({ options = [], name, open, setOpen, disabled = false }: Props) => {
  const [focusName, setFocusName] = useState("");
  const { control, watch, setValue } = useFormContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selectBoxRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
  });
  const theme = useTheme();
  const currentSizeList = watch(name) || [];
  const lang = window.localStorage.getItem("language");

  const { remove, append } = useFieldArray({
    control,
    name,
  }) as any;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setFocusName(name);
    if (options?.length === 0) return;

    setOpen(!open);
  };

  const handleChange = (sizeId: number, nameKo: string) => {
    const existingIndex = currentSizeList.findIndex(
      (item: any) => item.size_id === sizeId,
    );

    if (existingIndex !== -1) {
      remove(existingIndex);
    } else {
      append({ size_id: sizeId, amount: 0, size_name: nameKo });
    }
  };

  const handleDeleteSize = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    if (focusName !== name) {
      setOpen(false);
    }
  }, [focusName, name, setOpen]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        selectBoxRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const updateDropdownPosition = () => {
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownMaxHeight = 256;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
      const top = openUpward
        ? Math.max(8, rect.top - dropdownMaxHeight)
        : rect.bottom;
      const maxLeft = Math.max(8, window.innerWidth - rect.width - 8);

      setDropdownPos({
        top,
        left: Math.min(Math.max(rect.left, 8), maxLeft),
        width: rect.width,
        openUpward,
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [open]);

  return (
    <Container>
      <Button
        onClick={handleToggle}
        type="button"
        isDisabled={disabled}
        selectedValue={
          disabled
            ? "위에서 사이즈 체계를 먼저 선택해주세요"
            : currentSizeList?.length > 0
              ? currentSizeList?.map((v: any) => v.size_name).join(", ")
              : "선택"
        }
        ref={buttonRef}>
        {disabled
          ? "위에서 사이즈 체계를 먼저 선택해주세요"
          : currentSizeList[currentSizeList.length - 1]?.size_name || "선택"}
        <Image
          src={
            open
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>
      {open &&
        ReactDOM.createPortal(
          <SelectBox
            ref={selectBoxRef}
            top={dropdownPos.top}
            left={dropdownPos.left}
            width={dropdownPos.width}
            openUpward={dropdownPos.openUpward}
            initial={{ opacity: 0, y: dropdownPos.openUpward ? 10 : -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: dropdownPos.openUpward ? 10 : -10 }}
            transition={{ duration: 0.1 }}>
            {options.map((item, index) => (
              <OptionItem
                key={index}
                type="button"
                last={index === options.length - 1}
                onClick={() =>
                  handleChange(
                    item.code,
                    lang === "en" && item.name_en ? item.name_en : item.name_ko,
                  )
                }
                selected={currentSizeList.some(
                  (field: any) => field.size_id === item.code,
                )}>
                {lang === "en" && item.name_en ? item.name_en : item.name_ko}

                {currentSizeList.some(
                  (field: any) => field.size_id === item.code,
                ) && <Image src={theme.image.check_green.default} />}
              </OptionItem>
            ))}
          </SelectBox>,
          document.body,
        )}

      <ChangeQuantityContainer>
        {currentSizeList.map((item: any, index: number) => (
          <QuantityBox key={index}>
            {item.size_name}
            <label>사이즈 SKU <input maxLength={100} value={watch(`${name}.${index}.size_sku`) || ""} onChange={(event) => setValue(`${name}.${index}.size_sku`, event.target.value)} style={{ border: "1px solid #ccc", padding: "0.8rem", maxWidth: "13rem" }} /></label>
            <QuantityInputBox>
              <NumberInput
                name={`${name}.${index}.amount`}
                type="number"
                mode="numeric"
                maxWidth="10rem"
                dir="rtl"
                value={watch(`${name}.${index}.amount`)}
              />
              <span>갯수</span>

              <button onClick={() => handleDeleteSize(index)} type="button">
                <Image src={theme.image.close_round.default} size="2rem" />
              </button>
            </QuantityInputBox>
          </QuantityBox>
        ))}
      </ChangeQuantityContainer>
    </Container>
  );
};

export default SizeDropdown;
